import React from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Truck } from "lucide-react";
import { useQuotePopup } from "../context/QuotePopupContext";

const BANGALORE_IMG =
  "https://images.unsplash.com/photo-1697130383976-38f28c444292?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxiYW5nYWxvcmUlMjBwYWxhY2V8ZW58MHx8fHwxNzgxMjYxMjMwfDA&ixlib=rb-4.1.0&q=85";

const AREAS = [
  "Indiranagar", "Koramangala", "HSR Layout", "Whitefield", "Yelahanka",
  "JP Nagar", "Jayanagar", "Rajajinagar", "Hebbal", "Electronic City",
  "Marathahalli", "Banashankari", "Malleshwaram", "BTM", "Bommanahalli",
];

export const BangalorePresence = () => {
  const { openPopup } = useQuotePopup();
  return (
    <section
      id="bangalore"
      data-testid="bangalore-section"
      className="relative section-pad overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={BANGALORE_IMG}
          alt="Bangalore skyline at twilight"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#07271E]/85" />
      </div>

      <div className="container-luxury relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="heading-eyebrow">
                <span className="line" />
                <span className="text">Bangalore Presence</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-white leading-tight">
                Kerala's finest cardamom.
                <br />
                <span className="italic font-light text-[#C9A227]">
                  Delivered across Bangalore.
                </span>
              </h2>
              <p className="mt-6 text-white/80 text-base md:text-lg font-light leading-relaxed max-w-2xl">
                Same-week wholesale delivery to every commercial pocket — from
                Indiranagar boutiques to Electronic City canteens — backed by a
                local warehouse and a dedicated B2B logistics fleet.
              </p>

              <div className="mt-8 grid grid-cols-3 gap-3 max-w-lg">
                {[
                  { icon: MapPin, k: "120+", v: "Routes Covered" },
                  { icon: Clock, k: "48 hr", v: "Avg Dispatch" },
                  { icon: Truck, k: "Daily", v: "Bulk Logistics" },
                ].map((s, i) => (
                  <div
                    key={i}
                    className="glass-dark p-4 text-center"
                  >
                    <s.icon
                      className="w-5 h-5 text-[#C9A227] mx-auto"
                      strokeWidth={1.4}
                    />
                    <div className="font-serif text-2xl text-white mt-2">{s.k}</div>
                    <div className="label-overline text-white/60 mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="glass-dark p-7 md:p-9"
            >
              <div className="label-overline text-[#C9A227]">Service Areas</div>
              <h3 className="font-serif text-2xl text-white mt-2">
                We deliver across
              </h3>
              <div className="mt-6 flex flex-wrap gap-2">
                {AREAS.map((a) => (
                  <span
                    key={a}
                    className="px-3 py-1.5 text-xs text-white/80 border border-white/15 hover:border-[#C9A227] hover:text-[#C9A227] transition-colors duration-300 font-light"
                  >
                    {a}
                  </span>
                ))}
                <span className="px-3 py-1.5 text-xs text-[#C9A227] border border-[#C9A227] font-light italic">
                  + 80 more
                </span>
              </div>
              <button
                onClick={openPopup}
                data-testid="bangalore-cta"
                className="btn-gold mt-7 w-full"
              >
                Check Delivery To Your Area
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
